import React from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";

const Section = styled.section`
  padding: 96px 0 48px;
  background: var(--nd-black);
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute; inset: 0;
    background-image: radial-gradient(circle, var(--nd-border) 1px, transparent 1px);
    background-size: 16px 16px;
    opacity: 0.4;
    pointer-events: none;
  }
  @media (max-width: 768px) { padding: 64px 0 32px; }
`;

const Inner = styled.div`
  position: relative;
  max-width: 880px;
`;

const Kicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin: 0 0 16px;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 48px;
  font-weight: 500;
  color: var(--nd-text-display);
  line-height: 1.05;
  letter-spacing: -0.02em;
  margin: 0 0 16px;
  @media (max-width: 768px) { font-size: 32px; }
`;

const Description = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0 0 32px;
  max-width: 640px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const MetaCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const MetaValueDoto = styled.span`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 36px;
  font-weight: 700;
  color: var(--nd-text-display);
  line-height: 1;
  letter-spacing: -0.02em;
`;

const MetaValue = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 16px;
  color: var(--nd-text-primary);
`;

const DIFFICULTY_COLOR = {
  beginner: "var(--nd-success)",
  intermediate: "var(--nd-warning)",
  advanced: "var(--nd-accent)",
};

const DifficultyBadge = styled.span`
  font-family: 'Press Start 2P', 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => DIFFICULTY_COLOR[p.level] || "var(--nd-text-primary)"};
  border: 1px solid ${(p) => DIFFICULTY_COLOR[p.level] || "var(--nd-border-visible)"};
  padding: 6px 10px;
  border-radius: 4px;
  display: inline-block;
`;

const Instructor = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.04em;
  color: var(--nd-text-secondary);
  margin: 16px 0 0;
`;

const ModuleHero = ({ module }) => (
  <Section>
    <Container>
      <Inner>
        <Kicker>{module.kicker}</Kicker>
        <Title>{module.title}</Title>
        <Description>{module.description}</Description>
        <MetaRow>
          <MetaCell>
            <MetaLabel>Duration</MetaLabel>
            <MetaValueDoto>{module.durationHours}h</MetaValueDoto>
          </MetaCell>
          <MetaCell>
            <MetaLabel>Lessons</MetaLabel>
            <MetaValueDoto>{module.lessonCount}</MetaValueDoto>
          </MetaCell>
          <MetaCell>
            <MetaLabel>Level</MetaLabel>
            <MetaValue>
              <DifficultyBadge level={module.difficulty}>{module.difficulty}</DifficultyBadge>
            </MetaValue>
          </MetaCell>
        </MetaRow>
        <Instructor>
          Instructor · {module.instructor} — {module.instructorRole}
        </Instructor>
      </Inner>
    </Container>
  </Section>
);

export default ModuleHero;
